import { z } from 'zod';

// ─── Display name ───────────────────────────────────────────────────
// Shared by the API (set-password) and the web app so the rules for a
// user-facing display name can never drift between client and server.

export const DISPLAY_NAME_MIN_LENGTH = 1;
export const DISPLAY_NAME_MAX_LENGTH = 80;

/** Reason a display name was rejected — maps to a user-facing message per surface. */
export type DisplayNameProblem = 'required' | 'tooLong' | 'noLetter';

/** Unicode-aware: any letter in any script satisfies the "real name" check. */
const LETTER_PATTERN = /\p{L}/u;

/**
 * Returns the first reason a display name is invalid, or `null` if it is
 * acceptable. Operates on the trimmed value.
 */
export function checkDisplayName(name: string): DisplayNameProblem | null {
  const trimmed = name.trim();
  if (trimmed.length < DISPLAY_NAME_MIN_LENGTH) return 'required';
  if (trimmed.length > DISPLAY_NAME_MAX_LENGTH) return 'tooLong';
  if (!LETTER_PATTERN.test(trimmed)) return 'noLetter';
  return null;
}

/** Default English messages — used server-side where there is no i18n layer. */
export const DISPLAY_NAME_PROBLEM_MESSAGES: Record<DisplayNameProblem, string> = {
  required: 'Name is required',
  tooLong: `Name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer`,
  noLetter: 'Name must include at least one letter',
};

/**
 * Zod schema for a user-facing display name. Trims input and enforces the
 * length bounds plus the "must contain a letter" rule (rejects symbol- or
 * digit-only junk like "*-*-*-*").
 */
export const DisplayNameSchema = z
  .string()
  .trim()
  .superRefine((name, ctx) => {
    const problem = checkDisplayName(name);
    if (problem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: DISPLAY_NAME_PROBLEM_MESSAGES[problem] });
    }
  });
